import { useEffect, useRef, useState } from "react"

// Redesign option 1c (see design_handoff_about_page/README.md): compress the old
// 5-paragraph intro into one headline, put Experience immediately underneath as a
// collapsible list so it's visible without scrolling far. Nav is handled globally
// by <Nav /> (hamburger + overlay), so this page renders content only.

const experience = [
  {
    id: "okx",
    period: "2024 — Now",
    logo: "/logos/OKX.png",
    company: "OKX · Singapore",
    titles: ["Design Director", "Senior Design Manager"],
    bulleted: true,
    lines: [
      "Led Web3 Growth design — Outcomes (Prediction Markets), Boost (Web3 rewards ecosystem), DEX Affiliate.",
      "Established AI-native design workflows to accelerate design exploration, prototyping, and iteration.",
      "Leading the Web3 Growth & OKLink design team to build scalable growth ecosystems that drive user activation, retention, and on-chain value creation.",
      "Led DeFi design — DeFi 2.0 revamp, staking, yield, lending, and protocol integrations.",
      "Scaled team 3→9 across Growth, DeFi, and OKLink. Acted as both people leader and principal IC across complex problem spaces.",
    ],
    defaultOpen: true,
  },
  {
    id: "grab",
    period: "2016 — 2024",
    logo: "/logos/Grab.png",
    company: "Grab · Singapore",
    titles: [
      "Principal Product Designer",
      "Design Manager II",
      "Lead Product Designer",
      "Senior Product Designer",
    ],
    bulleted: false,
    lines: [
      "Design leadership across Grab Commerce and Grab Financial Group, driving design strategy and long-term experience vision in partnership with senior product and business leadership. Led the entire consumer-facing user experience of Grab App.",
      "Managed design for the consumer payment experience and GrabHealth team. Built and led GrabHealth design from 0→1 — defining team structure, experience strategy, and design execution for online-to-offline healthcare solutions across SEA. Drove Pay with Grab, embedding scalable payment patterns across transportation, delivery, and financial services.",
      "Design owner of the first-generation Grab Super App, establishing foundational navigation, interaction patterns, and experience principles that scaled across new services and markets. Led and shipped the major Grab App redesign, redefining core user flows and interaction models to support the transition from single-service to Super App.",
    ],
    keyProducts:
      "Pay with Grab strategy · Future of GrabFin · GrabPay Card · PayLater · Gamified payment · Grab passenger app redesign · Grab everyday super app homepage · Multi-stop rides · GrabFood merchant app v1 · Chat experience",
  },
  {
    id: "indeed",
    period: "2019",
    logo: "/logos/indeed.png",
    company: "Indeed · Singapore",
    titles: ["UX Designer"],
    bulleted: false,
    lines: [
      "First UX design hire for the Singapore team. Defined design process, culture, and practice from scratch.",
    ],
  },
  {
    id: "yahoo-2015",
    period: "2015 — 2016",
    logo: "/logos/Yahoo.png",
    company: "Yahoo! · Taiwan",
    titles: ["Product Designer"],
    bulleted: false,
    lines: [
      "Led UX/UI design for Movies app (Android/iOS), Shopping app, Comparison Shopping (iOS), Store app, and dictionary concept.",
    ],
  },
  {
    id: "trend",
    period: "2015",
    logo: "/logos/trend.png",
    company: "Trend Micro · Taiwan",
    titles: ["UI Engineer"],
    bulleted: false,
    lines: [
      "Mobile Security 6.0 optimizer design. Proposed the Trend Micro design language for all consumer products.",
    ],
  },
  {
    id: "dlink",
    period: "2012 — 2014",
    logo: "/logos/dlink.png",
    company: "D-Link · Taiwan",
    titles: ["Interaction Designer"],
    bulleted: false,
    lines: ["UX design for D-Link Cloud services — camera, NAS, and router products."],
  },
  {
    id: "yahoo-2012",
    period: "2012",
    logo: "/logos/Yahoo.png",
    company: "Yahoo! · Taiwan",
    titles: ["Interaction Design Intern"],
    bulleted: false,
    lines: ["Yahoo! Asia youth social communications research project."],
  },
]

export default function About() {
  const [openIds, setOpenIds] = useState(
    () => new Set(experience.filter((e) => e.defaultOpen).map((e) => e.id))
  )
  const panelRef = useRef(null)
  const headerRef = useRef(null)

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Subtle parallax on the header as the panel scrolls: the headline drifts
  // upward slower than the content underneath it and fades out, so it reads
  // as a layer with depth rather than static text (sleek scroll feel).
  useEffect(() => {
    const panel = panelRef.current
    const header = headerRef.current
    if (!panel || !header) return
    let raf = null
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        const y = panel.scrollTop
        const translate = Math.min(y * 0.3, 100)
        const fade = Math.max(1 - y / 380, 0)
        header.style.transform = `translate3d(0, ${-translate}px, 0)`
        header.style.opacity = fade
        raf = null
      })
    }
    panel.addEventListener("scroll", onScroll, { passive: true })
    return () => panel.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <div className="page-panel" ref={panelRef}>
      <div className="panel-inner">

        <div className="panel-header" ref={headerRef}>
          <div className="panel-tag">About</div>
          <h2 className="panel-title">Rice Tseng</h2>
          <p className="about-bio">
            Design Director at OKX, leading Web3 Growth.{" "}
            <span className="about-bio-muted">
              Previously Principal Product Designer at Grab. Passionate about building
              products people love and leading AI transformation across the organization.
            </span>
          </p>
        </div>

        <div className="about-layout">
          <div>
            <div className="about-section-header">
              <span>Experience</span>
              <span className="about-section-header-hint">Click to expand</span>
            </div>

            <ul className="timeline">
              {experience.map((entry) => {
                const open = openIds.has(entry.id)
                const [currentTitle, ...earlierTitles] = entry.titles
                return (
                  <li className="timeline-item" key={entry.id}>
                    <button
                      type="button"
                      className="timeline-summary"
                      aria-expanded={open}
                      aria-controls={`exp-${entry.id}`}
                      onClick={() => toggle(entry.id)}
                    >
                      <span className="timeline-period">{entry.period}</span>
                      <span className="timeline-company-row">
                        <img
                          className="timeline-logo"
                          src={entry.logo}
                          alt={entry.company.split(" · ")[0]}
                        />
                        <span className="timeline-company">{entry.company}</span>
                      </span>
                      <span className="timeline-current-title">{currentTitle}</span>
                      <span className={`timeline-indicator${open ? " is-open" : ""}`} aria-hidden="true">
                        +
                      </span>
                    </button>

                    <div
                      id={`exp-${entry.id}`}
                      role="region"
                      className={`timeline-disclosure${open ? " open" : ""}`}
                    >
                      <div className="timeline-disclosure-inner">
                        <div className="timeline-content-inner">
                          {entry.titles.length > 1 && (
                            <div className="timeline-roles-list">
                              {[currentTitle, ...earlierTitles].map((title) => (
                                <div className="timeline-role-item" key={title}>
                                  <span className="timeline-role-title">{title}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {entry.bulleted ? (
                            <ul className="timeline-bullets">
                              {entry.lines.map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          ) : (
                            entry.lines.map((line, i) => (
                              <p
                                className="timeline-desc"
                                style={i > 0 ? { marginTop: 10 } : undefined}
                                key={i}
                              >
                                {line}
                              </p>
                            ))
                          )}

                          {entry.keyProducts && (
                            <p className="timeline-desc" style={{ marginTop: 10 }}>
                              <strong style={{ color: "var(--fg)", fontWeight: 500 }}>
                                Key products:
                              </strong>{" "}
                              {entry.keyProducts}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>

          <aside className="about-intro-meta">
            <img src="/profile.jpg" alt="Rice Tseng" className="about-profile-photo" />

            <div className="about-meta-item">
              <div className="about-meta-label">Currently</div>
              <div className="about-meta-value">
                Design Director<br />OKX · Web3 Growth
              </div>
            </div>
            <div className="about-meta-item">
              <div className="about-meta-label">Experience</div>
              <div className="about-meta-value">
                14+ years Product Design<br />7+ years Design Leadership
              </div>
            </div>
            <div className="about-meta-item">
              <div className="about-meta-label">Focus</div>
              <div className="about-meta-value">
                Web3 · FinTech · Super Apps<br />AI-transformation · 0→1 Products
              </div>
            </div>
            <div className="about-meta-item">
              <div className="about-meta-label">Location</div>
              <div className="about-meta-value">Singapore</div>
            </div>
            <div className="about-meta-item">
              <div className="about-meta-label">Podcast</div>
              <div className="about-meta-value">
                <a href="/podcast">Product Design 1 on 1 ↗</a>
              </div>
            </div>
            <div className="about-meta-item">
              <div className="about-meta-label">Elsewhere</div>
              <div className="about-meta-value" style={{ display: "flex", gap: 18 }}>
                <a href="https://x.com/ricetseng" target="_blank" rel="noopener noreferrer">X</a>
                <a href="https://linkedin.com/in/ricetseng" target="_blank" rel="noopener noreferrer">LinkedIn</a>
                <a href="https://medium.com/@ricetseng_0820" target="_blank" rel="noopener noreferrer">Medium</a>
              </div>
            </div>
          </aside>
        </div>

      </div>
    </div>
  )
}
